import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  CustomValidators,
  InformationBoxComponent,
} from '@principle-theorem/ng-principle-shared';
import {
  formControlChanges$,
  InputSearchFilter,
  LoaderButtonComponent,
  NgCommonModule,
  NgDialogModule,
  toSearchStream,
  TypedFormControl,
  TypedFormGroup,
} from '@principle-theorem/ng-shared';
import {
  getMohCodeExtendedData,
  IHealthcareClaim,
  IInvoice,
  IMohClaimableItem,
  IMohClaimFundingRequest,
  IMohPreApprovalFundingRequest,
  IMohSchool,
  IMohSdsIndividualTreatmentData,
  IPatient,
  IPractice,
  IStaffer,
  MOH_CLAIM_TYPE_LABEL_MAP,
  MohClaimType,
  MohGenerateFormRequest,
  MohSdsTreatmentReason,
  NZ_SCHOOLS,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  filterUndefined,
  Firestore,
  getEnumValues,
  getError,
  httpsCallable,
  serialise,
  SerialisedData,
  snapshot,
  titlecase,
  unserialise,
  WithRef,
} from '@principle-theorem/shared';
import { compact, isEqual } from 'lodash';
import { combineLatest, Observable, of } from 'rxjs';
import { map, take } from 'rxjs/operators';
import {
  IMohAgreementHolderForm,
  MohAgreementHolderForm,
} from '../../../moh/moh-agreement-holder-form';
import { MohClaimsService } from '../../../moh/moh-claims.service';
import {
  IMohCategorisedTreatmentData,
  MohHelpers,
} from '../../../moh/moh-helpers';
import { MohAgreementHolderFormComponent } from '../moh-agreement-holder-form/moh-agreement-holder-form.component';
import { MohTreatmentListCardComponent } from '../moh-treatment-list-card/moh-treatment-list-card.component';
import { MohSdsClaimFormComponent } from './moh-sds-claim-form/moh-sds-claim-form.component';
import { MohClaimApprovalService } from '../../../moh/moh-claim-approval.service';

export interface IMohSendClaimForm {
  isAnnualConsultation: boolean;
  claimType: MohClaimType;
  school: IMohSchool;
  nhiNumber: string;
  agreementHolder: IMohAgreementHolderForm;
  approvalRequest?: WithRef<IMohPreApprovalFundingRequest>;
  approvalNumber?: string;
}

export type SdsClaimForm = Pick<
  IMohSdsIndividualTreatmentData,
  | 'schoolOrOralHealthCentre'
  | 'treatmentReason'
  | 'schoolTown'
  | 'usualOralHealthPractitioner'
  | 'practitionerTown'
  | 'cohsReferralNumber'
>;

export interface IMohSendClaimDialogData {
  claimRequestRef: DocumentReference<IMohClaimFundingRequest>;
  claimUid: string;
  invoice: WithRef<IInvoice>;
  practice: WithRef<IPractice>;
  practitioner: WithRef<IStaffer>;
  patient: WithRef<IPatient>;
  claimItems: IMohClaimableItem[];
  claim: IHealthcareClaim;
  approvalRequests: WithRef<IMohPreApprovalFundingRequest>[];
}

@Component({
  selector: 'pr-moh-send-claim-dialog',
  imports: [
    InformationBoxComponent,
    LoaderButtonComponent,
    MatAutocompleteModule,
    MatCheckboxModule,
    MohAgreementHolderFormComponent,
    MohSdsClaimFormComponent,
    MohTreatmentListCardComponent,
    NgCommonModule,
    NgDialogModule,
  ],
  templateUrl: './moh-send-claim-dialog.component.html',
  styleUrl: './moh-send-claim-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MohClaimsService, MohClaimApprovalService],
})
export class MohSendClaimDialogComponent {
  private _claimService = inject(MohClaimsService);
  private _dialogRef = inject(
    MatDialogRef<
      MohSendClaimDialogComponent,
      DocumentReference<IMohClaimFundingRequest>
    >
  );
  data = inject<IMohSendClaimDialogData>(MAT_DIALOG_DATA);

  agreementHolderForm = MohAgreementHolderForm.init();
  form = new TypedFormGroup<IMohSendClaimForm>({
    isAnnualConsultation: new TypedFormControl<boolean>(true),
    claimType: new TypedFormControl<MohClaimType>(MohClaimType.OHSA),
    school: new TypedFormControl<IMohSchool>(undefined),
    nhiNumber: new TypedFormControl<string>('', Validators.maxLength(7)),
    agreementHolder: this.agreementHolderForm,
    approvalRequest: new TypedFormControl<
      WithRef<IMohPreApprovalFundingRequest> | undefined
    >(undefined),
    approvalNumber: new TypedFormControl<string>(undefined),
  });
  sdsForm = new TypedFormGroup<SdsClaimForm>({
    schoolOrOralHealthCentre: new TypedFormControl<string>(''),
    treatmentReason: new TypedFormControl<MohSdsTreatmentReason>(),
    schoolTown: new TypedFormControl<string>(''),
    usualOralHealthPractitioner: new TypedFormControl<string>(''),
    practitionerTown: new TypedFormControl<string>(''),
    cohsReferralNumber: new TypedFormControl<string>(''),
  });

  allSchools = NZ_SCHOOLS;
  claimTypes = getEnumValues(MohClaimType);
  schoolSearch: InputSearchFilter<IMohSchool>;

  submitDisabled$: Observable<boolean>;
  treatmentData$: Observable<IMohCategorisedTreatmentData>;
  isSdsClaimType$: Observable<boolean>;

  loading = signal(false);

  constructor() {
    this.form.patchValue({
      school: this.data.patient.metadata?.moh?.school,
      nhiNumber: this.data.patient.nationalHealthIndexNumber,
    });

    this.form.controls.claimType.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((formType) => {
        if (formType !== MohClaimType.OHSA) {
          this.form.controls.isAnnualConsultation.setValue(false);
          this.form.controls.isAnnualConsultation.disable();
          return;
        }
        this.form.controls.isAnnualConsultation.enable();
      });

    const schoolSearchStream = toSearchStream(this.form.controls.school);
    this.schoolSearch = new InputSearchFilter<IMohSchool>(
      of(this.allSchools),
      schoolSearchStream,
      ['schoolName']
    );

    this.treatmentData$ = combineLatest([
      formControlChanges$(this.form.controls.claimType).pipe(filterUndefined()),
      formControlChanges$(this.form.controls.isAnnualConsultation),
    ]).pipe(
      map(([claimType, isAnnualConsultation]) =>
        MohHelpers.getCategorisedTreatmentData(
          this.data.claimItems,
          claimType,
          !!isAnnualConsultation
        )
      )
    );

    this.treatmentData$
      .pipe(take(1), takeUntilDestroyed())
      .subscribe((treatmentData) => {
        this.form.controls.isAnnualConsultation.setValue(
          !!treatmentData.showStandardPackage
        );
      });

    this.treatmentData$
      .pipe(takeUntilDestroyed())
      .subscribe((treatmentData) => {
        const validators = treatmentData.showRequiresApproval
          ? CustomValidators.requireOneOf(['approvalRequest', 'approvalNumber'])
          : [];
        this.form.setValidators(validators);
        this.form.updateValueAndValidity();
      });

    this.isSdsClaimType$ = formControlChanges$(
      this.form.controls.claimType
    ).pipe(map((claimType) => claimType === MohClaimType.SDS));
  }

  async submit(): Promise<void> {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    try {
      this.loading.set(true);
      const formData = this.form.getRawValue();
      const patient = await this._updatePatient(formData);
      const request = await this._claimService.buildClaimFormRequest(
        this.data,
        formData,
        this.sdsForm.getRawValue(),
        patient
      );
      if (!request) {
        return;
      }

      const response = await snapshot(
        httpsCallable<
          SerialisedData<MohGenerateFormRequest>,
          SerialisedData<DocumentReference<IMohClaimFundingRequest> | undefined>
        >('http-moh-generateForm')(serialise(request))
      );
      if (!response) {
        throw new Error('Failed to generate MOH claim form');
      }

      const claimRef = unserialise(
        response
      ) as DocumentReference<IMohClaimFundingRequest>;
      const claim = await Firestore.getDoc(claimRef);
      const submittedRef = await this._claimService.signAndSubmitClaimRequest(
        claim,
        formData.approvalRequest
      );
      this._dialogRef.close(submittedRef);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(getError(error));
      this._dialogRef.close();
    } finally {
      this.loading.set(false);
    }
  }

  displayFn(school?: IMohSchool): string {
    return school ? school.schoolName : '';
  }

  getClaimTypeLabel(claimType: MohClaimType): string {
    return MOH_CLAIM_TYPE_LABEL_MAP[claimType];
  }

  getApprovalRequestLabel(
    request: WithRef<IMohPreApprovalFundingRequest>
  ): string {
    const claimType = MOH_CLAIM_TYPE_LABEL_MAP[request.data.claimType];
    const status = titlecase(request.status);
    return `${status} - ${claimType} ${request.data.approvalNumber}`;
  }

  claimTypeDisabled(claimType: MohClaimType): boolean {
    const extendedDataItems = compact(
      this.data.claimItems.map((item) =>
        getMohCodeExtendedData(item.serviceCode.code)
      )
    );

    return !extendedDataItems.some(
      (extendedData) => extendedData.claimTypes?.includes(claimType)
    );
  }

  updateComment(comments: string, codeUid?: string): void {
    if (!codeUid) {
      return;
    }
    this.data.claimItems = this.data.claimItems.map((item) => {
      if (item.serviceCode.uid === codeUid) {
        return { ...item, comments };
      }
      return item;
    });
  }

  private async _updatePatient(
    formData: IMohSendClaimForm
  ): Promise<WithRef<IPatient>> {
    const updatedPatient: WithRef<IPatient> = {
      ...this.data.patient,
      nationalHealthIndexNumber: formData.nhiNumber,
      metadata: {
        ...this.data.patient.metadata,
        moh: {
          ...this.data.patient.metadata?.moh,
          school: formData.school,
        },
      },
    };

    const isSchoolChanged = !isEqual(
      formData.school,
      this.data.patient.metadata?.moh?.school
    );
    const isNhiChanged =
      formData.nhiNumber !== this.data.patient.nationalHealthIndexNumber;
    if (!isSchoolChanged && !isNhiChanged) {
      return updatedPatient;
    }

    await Firestore.patchDoc(this.data.patient.ref, {
      nationalHealthIndexNumber: formData.nhiNumber,
      metadata: {
        ...this.data.patient.metadata,
        moh: {
          ...this.data.patient.metadata?.moh,
          school: formData.school,
        },
      },
    });
    return updatedPatient;
  }
}
